import type { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/cn';

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Yükleme alanının yaklaşık şekli: text/circle/block/card. */
  variant?: 'text' | 'circle' | 'block' | 'card';
}

/**
 * Skeleton — BRANDBOOK §6.14 (Sprint 28c hed_v3, 14A Shimmer).
 *
 * Linear gradient g100 → g200 → g100, shimmer 1.4s ease-in-out infinite.
 * Parent container `aria-busy="true"` ile birlikte kullanılır (BRANDBOOK §10.5).
 */
export function Skeleton({ variant = 'text', className = '', ...rest }: SkeletonProps) {
  const shape =
    variant === 'circle'
      ? 'rounded-full aspect-square'
      : variant === 'block'
        ? 'rounded-custel-md'
        : variant === 'card'
          ? 'rounded-custel-2xl h-[120px]'
          : 'rounded-custel-xs h-4';
  return (
    <div
      data-shape={variant}
      className={cn('custel-skeleton', shape, className)}
      aria-hidden="true"
      style={{
        background: 'linear-gradient(90deg, var(--custel-g100) 0%, var(--custel-g200) 30%, var(--custel-g100) 60%)',
        backgroundSize: '200px 100%',
        backgroundRepeat: 'no-repeat',
        animation: 'skeleton-shimmer 1.4s ease-in-out infinite',
      }}
      {...rest}
    />
  );
}

/** Tablo satırları için 3-5 satırlı placeholder. */
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-2 p-4" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Kart düzenleri için placeholder. */
export function CardSkeleton() {
  return (
    <div className="card-custel" aria-busy="true">
      <Skeleton className="mb-3 h-5 w-32" />
      <Skeleton className="h-8 w-24" />
    </div>
  );
}

/**
 * TopProgressBar — BRANDBOOK §6.14 (Sprint 28c).
 * Uzun batch işlemler (export, bulk approve) için indeterminate lime bar.
 */
export function TopProgressBar({ visible }: { visible: boolean }) {
  const { t } = useTranslation('components');
  if (!visible) return null;
  return (
    <div
      role="progressbar"
      aria-label={t('common:state.loading')}
      className="pointer-events-none fixed left-0 right-0 top-0 z-[3000] h-[3px] overflow-hidden bg-custel-g100"
    >
      <div
        className="absolute inset-0 w-1/2 bg-custel-lime"
        style={{ animation: 'progress-slide 1.4s ease-in-out infinite' }}
      />
    </div>
  );
}
